import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mastersApi } from "../lib/api";
import { useCompany } from "./reference";
import { useLoad } from "../ui/useLoad";
import { fuzzyFilter } from "../lib/fuzzy";
import { fmtAmount } from "../lib/money";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { useShortcuts } from "../lib/useShortcuts";
import { toUrl } from "../shell/routes";

// Accounts: type to filter (number, name, group, GSTIN, PAN), arrows, Enter to open, Alt+N for a new one.
export function Ledgers() {
  const { company, ref } = useCompany();
  const codes = ref?.company.account_codes ?? false;
  const nav = useNavigate();
  const { data, error, loading } = useLoad(() => mastersApi.ledgers(company.id), [company.id]);
  const [q, setQ] = useState("");
  const [inactive, setInactive] = useState(false);
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useMemo(() => {
    const rows = (data ?? []).filter((l) => inactive || l.active);
    return q.trim() ? fuzzyFilter(rows, q, (l) => `${l.number ?? ""} ${l.name} ${l.group_name} ${l.gstin ?? ""} ${l.pan ?? ""}`, 300) : rows;
  }, [data, q, inactive]);
  useEffect(() => setSel(0), [q, inactive]);
  const base = toUrl(`masters/ledgers`);
  const open = (id: string) => nav(`${base}/${id}`);
  useShortcuts([{ keys: "Alt+N", run: () => { if (ref?.can_edit) nav(`${base}/new`); } }]);

  return (
    <section className="page">
      <PageHead crumb="Accounting" title="Accounts" code="AC11">
        {ref?.can_edit && <button onClick={() => nav(`${base}/new`)}>New account <kbd>Alt+N</kbd></button>}
      </PageHead>
      <div className="toolbar">
        <input ref={input} autoFocus placeholder={codes ? "Filter by name, code, group, GSTIN or PAN" : "Filter by name, group, GSTIN or PAN"} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter accounts"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, list.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
            else if (e.key === "Enter" && list[sel]) { e.preventDefault(); open(list[sel].id); }
            else if (e.key === "Escape" && q) { e.preventDefault(); e.stopPropagation(); setQ(""); }
          }} />
        <label className="check"><input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} /> Show inactive</label>
        <span className="muted small">{list.length} of {data?.length ?? 0}</span>
      </div>
      <ErrorNote text={error?.message} />
      {loading && !data ? <Loading /> : list.length === 0 ? (
        <p className="muted">{data?.length ? "No account matches." : "No accounts yet. Press Alt+N to create the first."}</p>
      ) : (
        <table className="grid-table">
          <thead><tr>{codes && <th>Code</th>}<th>Name</th><th>Under</th><th className="num">Opening</th><th>GSTIN / PAN</th></tr></thead>
          <tbody>
            {list.map((l, i) => (
              <tr key={l.id} className={i === sel ? "sel" : ""} onClick={() => open(l.id)} onMouseEnter={() => setSel(i)}
                  ref={(el) => { if (i === sel && el) el.scrollIntoView({ block: "nearest" }); }}>
                {codes && <td className="mono muted">{l.number ?? ""}</td>}
                <td>{l.name}{!l.active && <span className="tag">inactive</span>}</td>
                <td className="muted">{l.group_name}</td>
                <td className="num">{l.opening ? `${fmtAmount(l.opening.amount)} ${l.opening.side}` : ""}</td>
                <td className="muted mono">{l.gstin ?? l.pan ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
